export const UPLOAD_DIALOG_COLORS = {
  primary: '#3b82f6',
  primaryHover: '#2563eb',
  primaryDark: '#162346',
  primaryDarkHover: '#1a2a52',
  white: '#ffffff',
  background: '#f8fafc',
  border: '#e2e8f0',
  borderLight: '#e5e7eb',
  borderDashed: '#d1d5db',
  textPrimary: '#1e293b',
  textSecondary: '#374151',
  textMuted: '#6b7280',
  textPlaceholder: '#9ca3af',
  error: '#ef4444',
  errorDark: '#dc2626',
  errorBorder: '#fca5a5',
  errorBorderHover: '#f87171',
  errorBackground: '#fef2f2',
  info: '#0284c7',
  infoBackground: '#f0f9ff',
  infoBorder: '#bae6fd',
  dragOver: '#eff6ff',
  disabled: '#e5e7eb',
  disabledText: '#9ca3af',
  chipBackground: '#dbeafe',
  chipText: '#1e40af',
  codeBackground: '#f3f4f6',
};

export const UPLOAD_DIALOG_STYLES = {
  paper: { borderRadius: 2, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' },
  title: {
    backgroundColor: UPLOAD_DIALOG_COLORS.white,
    borderBottom: `2px solid ${UPLOAD_DIALOG_COLORS.primary}`,
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    p: 2.5,
    fontWeight: 600,
    color: UPLOAD_DIALOG_COLORS.textPrimary
  },
  content: { p: 3, backgroundColor: UPLOAD_DIALOG_COLORS.background },
  uploadArea: {
    border: `2px dashed ${UPLOAD_DIALOG_COLORS.borderDashed}`,
    borderRadius: 2,
    p: 4,
    textAlign: 'center' as const,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': { borderColor: UPLOAD_DIALOG_COLORS.primary, backgroundColor: UPLOAD_DIALOG_COLORS.infoBackground }
  },
  uploadAreaDragOver: {
    border: `2px dashed ${UPLOAD_DIALOG_COLORS.primary}`,
    backgroundColor: UPLOAD_DIALOG_COLORS.dragOver
  },
  fileItem: { p: 2, display: 'flex', alignItems: 'center', gap: 2, border: `1px solid ${UPLOAD_DIALOG_COLORS.borderLight}` },
  progressBar: {
    height: 8,
    borderRadius: 4,
    backgroundColor: UPLOAD_DIALOG_COLORS.disabled,
    '& .MuiLinearProgress-bar': { backgroundColor: UPLOAD_DIALOG_COLORS.primary }
  },
  infoAlert: {
    backgroundColor: UPLOAD_DIALOG_COLORS.infoBackground,
    border: `1px solid ${UPLOAD_DIALOG_COLORS.infoBorder}`,
    '& .MuiAlert-icon': { color: UPLOAD_DIALOG_COLORS.info }
  },
  actions: { p: 3, borderTop: `1px solid ${UPLOAD_DIALOG_COLORS.border}`, backgroundColor: UPLOAD_DIALOG_COLORS.white, gap: 1 },
  cancelButton: {
    borderColor: UPLOAD_DIALOG_COLORS.borderDashed,
    color: UPLOAD_DIALOG_COLORS.textMuted,
    '&:hover': { borderColor: UPLOAD_DIALOG_COLORS.textPlaceholder, backgroundColor: '#f9fafb' }
  },
  submitButton: {
    backgroundColor: UPLOAD_DIALOG_COLORS.primaryDark,
    '&:hover': { backgroundColor: UPLOAD_DIALOG_COLORS.primaryDarkHover },
    '&:disabled': { backgroundColor: UPLOAD_DIALOG_COLORS.disabled, color: UPLOAD_DIALOG_COLORS.disabledText }
  }
};

export const DOCUMENT_VIEW_DIALOG_STYLES = {
  paper: { borderRadius: 2, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' },
  title: {
    backgroundColor: UPLOAD_DIALOG_COLORS.white,
    borderBottom: `2px solid ${UPLOAD_DIALOG_COLORS.primary}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    p: 2.5,
    fontWeight: 600,
    color: UPLOAD_DIALOG_COLORS.textPrimary
  },
  content: { p: 3, backgroundColor: UPLOAD_DIALOG_COLORS.background },
  card: {
    p: 3,
    backgroundColor: UPLOAD_DIALOG_COLORS.white,
    borderRadius: 2,
    border: `1px solid ${UPLOAD_DIALOG_COLORS.borderLight}`
  },
  cardWithFlex: {
    p: 3,
    backgroundColor: UPLOAD_DIALOG_COLORS.white,
    borderRadius: 2,
    border: `1px solid ${UPLOAD_DIALOG_COLORS.borderLight}`,
    display: 'flex',
    alignItems: 'center',
    gap: 3
  },
  fileIconBox: {
    fontSize: '3rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '80px',
    height: '80px',
    backgroundColor: UPLOAD_DIALOG_COLORS.infoBackground,
    borderRadius: 2,
    border: `2px solid ${UPLOAD_DIALOG_COLORS.infoBorder}`
  },
  chip: { backgroundColor: UPLOAD_DIALOG_COLORS.chipBackground, color: UPLOAD_DIALOG_COLORS.chipText },
  codePath: {
    fontWeight: 600,
    fontFamily: 'monospace',
    backgroundColor: UPLOAD_DIALOG_COLORS.codeBackground,
    px: 1,
    py: 0.5,
    borderRadius: 1,
    fontSize: '0.75rem'
  },
  viewButton: {
    backgroundColor: UPLOAD_DIALOG_COLORS.primary,
    '&:hover': { backgroundColor: UPLOAD_DIALOG_COLORS.primaryHover }
  },
  downloadButton: {
    borderColor: UPLOAD_DIALOG_COLORS.borderDashed,
    color: UPLOAD_DIALOG_COLORS.textSecondary,
    '&:hover': { borderColor: UPLOAD_DIALOG_COLORS.textPlaceholder, backgroundColor: '#f9fafb' }
  },
  deleteButton: {
    borderColor: UPLOAD_DIALOG_COLORS.errorBorder,
    color: UPLOAD_DIALOG_COLORS.errorDark,
    '&:hover': { borderColor: UPLOAD_DIALOG_COLORS.errorBorderHover, backgroundColor: UPLOAD_DIALOG_COLORS.errorBackground }
  },
  infoAlert: {
    backgroundColor: UPLOAD_DIALOG_COLORS.infoBackground,
    border: `1px solid ${UPLOAD_DIALOG_COLORS.infoBorder}`,
    '& .MuiAlert-icon': { color: UPLOAD_DIALOG_COLORS.info }
  },
  actions: { p: 3, pt: 2, borderTop: `1px solid ${UPLOAD_DIALOG_COLORS.border}`, backgroundColor: UPLOAD_DIALOG_COLORS.white },
  closeButton: {
    borderColor: UPLOAD_DIALOG_COLORS.borderDashed,
    color: UPLOAD_DIALOG_COLORS.textMuted,
    '&:hover': { borderColor: UPLOAD_DIALOG_COLORS.textPlaceholder, backgroundColor: '#f9fafb' }
  }
};

export const getFileIcon = (extension: string): string => {
  const ext = extension.toLowerCase();
  if (['.pdf'].includes(ext)) return '📄';
  if (['.doc', '.docx'].includes(ext)) return '📝';
  if (['.xls', '.xlsx'].includes(ext)) return '📊';
  if (['.png', '.jpg', '.jpeg', '.gif'].includes(ext)) return '🖼️';
  if (['.txt'].includes(ext)) return '📋';
  return '📎';
};

export const getFileTypeLabel = (extension: string): string => {
  const ext = extension.toLowerCase();
  switch (ext) {
    case '.pdf': return 'PDF Document';
    case '.doc':
    case '.docx': return 'Word Document';
    case '.xls':
    case '.xlsx': return 'Excel Spreadsheet';
    case '.png':
    case '.jpg':
    case '.jpeg':
    case '.gif': return 'Image File';
    case '.txt': return 'Text File';
    default: return 'Document';
  }
};

export const formatDate = (dateString?: string | null): string => {
  if (!dateString) return 'Unknown';
  return new Date(dateString).toLocaleString();
};

export const ALLOWED_EXTENSIONS = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.png', '.jpg', '.jpeg', '.gif', '.txt'];
export const MAX_FILE_SIZE = 10 * 1024 * 1024;

export const isSupportedFileType = (fileName: string): boolean => {
  const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
  return ALLOWED_EXTENSIONS.includes(extension);
};

export const isValidFileSize = (fileSize: number): boolean => fileSize <= MAX_FILE_SIZE;

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};
